<template>
    <div class="settings-card">
        <div class="card-header">
            <h2>Appearance</h2>
        </div>
        <div class="card-content">
            <div class="setting-row">
                <div class="setting-info">
                    <label class="setting-label">Theme</label>
                    <span class="setting-description">
                        Choose your preferred color scheme
                    </span>
                </div>
                <div class="theme-selector">
                    <button
                        v-for="themeOption in themeOptions"
                        :key="themeOption.value"
                        @click="$emit('update-theme', themeOption.value)"
                        class="theme-option"
                        :class="{
                            active: currentTheme === themeOption.value,
                            [themeOption.class]: true,
                        }"
                    >
                        <span class="theme-icon">
                            <component :is="themeOption.icon" :size="40"/>
                        </span>
                        <span class="theme-name">{{ themeOption.label }}</span>
                    </button>
                </div>
            </div>

            <div class="setting-row">
                <div class="setting-info">
                    <label class="setting-label">
                        Exam Mode
                        <span class="tier-badge tier-2" :class="{ locked: !canUseExamMode }">{{ getTierDisplayName('TIER 2') }}</span>
                    </label>
                    <span class="setting-description">
                        Custom fonts and styles for an authentic HSC exam experience
                    </span>
                </div>
                <div class="toggle-wrapper">
                    <label class="toggle-switch" :class="{ disabled: !canUseExamMode }">
                        <input
                            type="checkbox"
                            :checked="examMode"
                            :disabled="!canUseExamMode"
                            @change="$emit('update:examMode', $event.target.checked)"
                        />
                        <span class="toggle-slider"></span>
                    </label>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue';
import { Sun, Moon, SunMoon } from 'lucide-vue-next';
import { useAuthStore } from '@/stores/auth';
import { getTierDisplayName } from '@/config/tiers';

export default {
    name: 'AppearanceCard',
    props: {
        currentTheme: {
            type: String,
            default: 'dark'
        },
        examMode: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update-theme', 'update:examMode'],
    setup() {
        const authStore = useAuthStore();

        const themeOptions = [
            { value: 'light', label: 'Light', icon: Sun, class: 'theme-light' },
            { value: 'dark', label: 'Dark', icon: Moon, class: 'theme-dark' },
            { value: 'auto', label: 'Auto', icon: SunMoon, class: 'theme-auto' }
        ];

        const canUseExamMode = computed(() => authStore.hasTierAccess('TIER 2'));

        return {
            themeOptions,
            canUseExamMode,
            getTierDisplayName
        };
    }
};
</script>

<style scoped>
.settings-card {
    background: var(--bg-primary);
    border: 1px solid rgba(var(--accent-rgb), 0.6);
    overflow: hidden;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--accent);
}

.card-header h2 {
    margin: 0;
    color: var(--text-secondary);
    font-size: 24px;
}

.card-content {
    padding: 30px;
    display: flex;
    flex-direction: column;
    gap: 30px;
}

.setting-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
}

.setting-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.setting-label {
    color: var(--text-secondary);
    font-size: 16px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
}

.tier-badge {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 5px;
    border-radius: 3px;
    letter-spacing: 0.5px;
    transition: all 0.2s;
}

/* Tier 1 - Blue */
.tier-badge.tier-1 {
    background: linear-gradient(135deg, #64c8ff, #50a8e0);
    color: #1a1a1a;
}

/* Tier 2 - Purple */
.tier-badge.tier-2 {
    background: linear-gradient(135deg, #9333ea, #7c28c4);
    color: #fff;
}

/* Tier 3 - Gold/Accent */
.tier-badge.tier-3 {
    background: linear-gradient(135deg, var(--accent), #d4a855);
    color: #000;
}

/* Locked state - dimmed when user doesn't have access */
.tier-badge.locked {
    opacity: 0.6;
    filter: grayscale(30%);
}

.setting-description {
    color: rgba(var(--text-primary-rgb), 0.6);
    font-size: 14px;
    line-height: 1.4;
}

.theme-selector {
    display: flex;
    gap: 12px;
}

.theme-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    background: var(--bg-secondary);
    border: 2px solid rgba(var(--accent-rgb), 0.8);
    cursor: pointer;
    transition: all 0.2s;
    min-width: 90px;
}

.theme-option:hover {
    background: var(--bg-secondary);
    border-color: var(--text-secondary);
    transform: translateY(-2px);
}

.theme-option.active {
    background: rgba(var(--bg-primary-rgb), 0.8);
    border-color: var(--accent);
}

.theme-icon {
    font-size: 24px;
    color: var(--text-primary);
}

.theme-name {
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 500;
}

.toggle-wrapper {
    display: flex;
    align-items: center;
}

.toggle-switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
    cursor: pointer;
}

.toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--bg-primary);
    transition: 0.3s;
    border-radius: 34px;
    border: 2px solid var(--accent);
}

.toggle-slider:before {
    position: absolute;
    content: "";
    height: 24px;
    width: 24px;
    left: 3px;
    bottom: 3px;
    background-color: var(--accent);
    transition: 0.3s;
    border-radius: 50%;
}

input:checked + .toggle-slider {
    background-color: var(--bg-primary);
    border-color: var(--accent);
}

input:checked + .toggle-slider:before {
    transform: translateX(26px);
    background-color: var(--accent);
}

.toggle-switch.disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.toggle-switch.disabled .toggle-slider {
    cursor: not-allowed;
}

@media (max-width: 768px) {
    .card-header {
        padding: 15px 20px;
    }

    .card-content {
        padding: 20px;
    }

    .setting-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .theme-selector {
        width: 100%;
        justify-content: space-between;
    }

    .theme-option {
        flex: 1;
        min-width: 0;
    }

    .toggle-wrapper {
        align-self: flex-end;
    }
}
</style>
